import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as ContextMenu from '@radix-ui/react-context-menu';
import { AudioWaveform, Camera, Check, ChevronDown, Film, Image as ImageIcon, Info, LoaderCircle, Mic, Monitor, MoreHorizontal, Pencil, Plus, RefreshCw, Trash2, Volume2, VolumeX, X } from 'lucide-react';
import CreatorCapture from './CreatorCapture';
import { creatorMediaUrl } from '../../lib/creatorApi';
import { creatorUploadStageLabel } from '../../lib/creatorUpload';
import './creatorMediaBin.css';

const KIND = { video: 'VIDEO', audio: 'AUDIO', image: 'IMAGEN' };
const MEDIA_ACCEPT = { video: 'video/*', audio: 'audio/*', image: 'image/png,image/jpeg,image/webp' };
const interactive = target => target.closest('button,input,label,a,video,audio,[role="menuitem"]');
const positive = value => Number.isFinite(value) && value > 0;
const seconds = value => {
  if (!Number.isFinite(value) || value < 0) return 'No disponible';
  const whole = Math.floor(value);
  return `${String(Math.floor(whole / 60)).padStart(2, '0')}:${String(whole % 60).padStart(2, '0')}`;
};
const resolution = asset => positive(asset.width) && positive(asset.height) ? `${asset.width} x ${asset.height}` : 'Resolucion no disponible';
const filenameError = value => {
  if (!value.trim() || value.trim() === '.' || value.trim() === '..') return 'Escribe un nombre de archivo.';
  if (value.length > 160) return 'El nombre admite hasta 160 caracteres.';
  // Match the server filename boundary, including control characters and path separators.
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f/\\]/.test(value)) return 'El nombre no puede contener /, \\ ni caracteres de control.';
  return '';
};

function dependencies(project, asset) {
  const ids = new Set([asset.id]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const item of project.assets || []) {
      if (item.derivation && ids.has(item.derivation.source_asset_id) && !ids.has(item.id)) {
        ids.add(item.id); changed = true;
      }
    }
  }
  const clips = (project.tracks || []).flatMap(track => track.clips || []);
  const direct = clips.filter(clip => clip.asset_id === asset.id).length;
  const derivedClips = clips.filter(clip => clip.asset_id !== asset.id && ids.has(clip.asset_id)).length;
  const beatGrid = ids.has(project.beat_grid?.source_asset_id) ? 1 : 0;
  return { direct, derived: ids.size - 1, derivedClips, beatGrid, used: direct + derivedClips + beatGrid > 0 || ids.size > 1 };
}

function recordingOrigin(filename = '') {
  if (/^captura-screen-/i.test(filename)) return { label: 'SCREEN', Icon: Monitor };
  if (/^captura-camera-/i.test(filename)) return { label: 'CAMERA', Icon: Camera };
  if (/^captura-audio-/i.test(filename)) return { label: 'MIC', Icon: Mic };
  return null;
}

function proxyLabel(asset) {
  if (asset.proxy_status === 'ready') return 'Proxy listo';
  if (asset.proxy_status === 'not_required') return 'Original listo';
  if (['pending', 'processing', 'preparing'].includes(asset.proxy_status)) return 'Preparando proxy...';
  if (asset.proxy_status === 'failed') return 'Proxy no disponible';
  return asset.proxy_url ? 'Proxy disponible' : 'Original';
}

function IconButton({ label, children, ...props }) {
  return <button type="button" className="cmb-icon" title={label} aria-label={label} {...props}>{children}</button>;
}

function MediaThumbnail({ asset, session }) {
  const [failed, setFailed] = useState(false);
  const src = creatorMediaUrl(asset.thumbnail_url, session);
  useEffect(() => setFailed(false), [src]);
  const Icon = asset.kind === 'audio' ? AudioWaveform : asset.kind === 'video' ? Film : ImageIcon;
  return <span className={`cmb-thumbnail cmb-thumbnail-${asset.kind}`}>
    {src && !failed ? <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} /> : <Icon aria-hidden="true" />}
  </span>;
}

function MenuActions({ primitive: Primitive, disabled, canManage, canInsert, canProxy, onAction }) {
  return <>
    <Primitive.Item className="cmb-menu-item" disabled={disabled || !canInsert} onSelect={() => onAction('add')}><Plus />Agregar a timeline</Primitive.Item>
    <Primitive.Item className="cmb-menu-item" disabled={disabled || !canInsert} onSelect={() => onAction('insert')}><Plus />Insertar en el cabezal</Primitive.Item>
    <Primitive.Item className="cmb-menu-item" disabled={disabled || !canInsert} onSelect={() => onAction('overwrite')}><RefreshCw />Sobrescribir en el cabezal</Primitive.Item>
    <Primitive.Item className="cmb-menu-item" onSelect={() => onAction('source')}><Film />Vista previa de fuente</Primitive.Item>
    <Primitive.Item className="cmb-menu-item" onSelect={() => onAction('info')}><Info />Informacion</Primitive.Item>
    {canManage && <>
      <Primitive.Separator className="cmb-menu-separator" />
      <Primitive.Item className="cmb-menu-item" disabled={disabled} onSelect={() => onAction('rename')}><Pencil />Renombrar</Primitive.Item>
      <Primitive.Item className="cmb-menu-item" disabled={disabled} onSelect={() => onAction('replace')}><RefreshCw />Reemplazar archivo</Primitive.Item>
      {canProxy && <Primitive.Item className="cmb-menu-item" disabled={disabled} onSelect={() => onAction('proxy')}><Film />Crear proxy</Primitive.Item>}
      <Primitive.Separator className="cmb-menu-separator" />
      <Primitive.Item className="cmb-menu-item cmb-danger" disabled={disabled} onSelect={() => onAction('delete')}><Trash2 />Eliminar medio</Primitive.Item>
    </>}
  </>;
}

function Modal({ title, description, onClose, children, source = false, returnFocus }) {
  return <Dialog.Root open onOpenChange={open => { if (!open) onClose(); }}>
    <Dialog.Portal>
      <Dialog.Overlay className="cmb-overlay" />
      <Dialog.Content className={`cmb-dialog ${source ? 'cmb-source-dialog' : ''}`} onKeyDown={event => event.stopPropagation()}
        onCloseAutoFocus={event => { event.preventDefault(); if (returnFocus?.isConnected) returnFocus.focus(); }}>
        <div className="cmb-dialog-heading">
          <Dialog.Title className="cmb-dialog-title">{title}</Dialog.Title>
          <Dialog.Close asChild><IconButton label="Cerrar dialogo"><X /></IconButton></Dialog.Close>
        </div>
        <Dialog.Description className="cmb-dialog-description">{description}</Dialog.Description>
        {children}
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}

function MediaInfo({ asset, project }) {
  const media = asset.media_info || {};
  const use = dependencies(project, asset);
  const parent = project.assets?.find(item => item.id === asset.derivation?.source_asset_id);
  const fields = [
    ['Tipo', KIND[asset.kind] || asset.kind],
    ['Contenedor', media.container],
    ...(asset.kind !== 'image' ? [['Duracion', positive(asset.duration) ? `${seconds(asset.duration)} (${asset.duration} s)` : null]] : []),
    ...(asset.kind !== 'audio' ? [['Resolucion', positive(asset.width) && positive(asset.height) ? resolution(asset) : null]] : []),
    ...(asset.kind === 'video' ? [['Codec de video', media.video_codec], ['FPS', positive(asset.fps) ? asset.fps : 'FPS variable/no declarado']] : []),
    ['Audio', asset.has_audio === true ? 'Con audio' : asset.has_audio === false ? 'Sin audio' : 'No disponible'],
    ...(asset.has_audio ? [['Codec de audio', media.audio_codec], ['Canales', positive(media.channels) ? media.channels : null], ['Frecuencia de muestreo', positive(media.sample_rate) ? `${media.sample_rate} Hz` : null]] : []),
    ['Tamano', positive(asset.size_bytes) ? `${(asset.size_bytes / (1024 * 1024)).toFixed(2)} MB` : null],
    ['Vista previa', asset.preview_mode === 'direct' ? 'Original' : asset.preview_mode === 'proxy' ? 'Proxy' : null],
    ['Estado', proxyLabel(asset)],
    ['Uso en timeline', `${use.direct} clips`],
    ['Medios derivados', use.derived],
    ...(parent ? [['Fuente', parent.filename], ['Operacion', asset.derivation.operation]] : []),
  ];
  return <dl className="cmb-info">{fields.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value ?? 'No disponible'}</dd></div>)}</dl>;
}

function RenameForm({ asset, busy, error, onSave, onCancel, onClearError }) {
  const [filename, setFilename] = useState(asset.filename);
  const issue = filenameError(filename);
  const errorId = useId();
  return <form className="cmb-form" onSubmit={event => { event.preventDefault(); if (!issue && !busy) onSave(filename.trim()); }}>
    <label className="cmb-field"><span>Nombre del archivo</span><input type="text" aria-label="Nombre del archivo" value={filename} maxLength={160} autoComplete="off" disabled={busy}
      aria-invalid={Boolean(issue || error)} aria-describedby={issue || error ? errorId : undefined}
      onChange={event => { setFilename(event.target.value); onClearError(); }} onFocus={event => event.target.select()} /></label>
    {(issue || error) && <p id={errorId} role="alert" className="cmb-error">{issue || error}</p>}
    <div className="cmb-dialog-actions"><button type="button" className="cmb-button" onClick={onCancel}>Cancelar</button>
      <button type="submit" className="cmb-button cmb-primary" disabled={busy || Boolean(issue)}>{busy ? <LoaderCircle className="cmb-spin" /> : <Check />}Guardar nombre</button></div>
  </form>;
}

function SourceMonitor({ asset, session, busy, error, onInsert, onCancel }) {
  const [duration, setDuration] = useState(positive(asset.duration) ? asset.duration : 0);
  const [start, setStart] = useState('0');
  const [end, setEnd] = useState(positive(asset.duration) ? String(asset.duration) : '');
  const [previewError, setPreviewError] = useState('');
  const [fallback, setFallback] = useState(false);
  const original = creatorMediaUrl(asset.url, session);
  const proxy = creatorMediaUrl(asset.proxy_url, session);
  const src = fallback ? proxy : asset.preview_mode === 'direct' ? original || proxy : proxy || original;
  const isImage = asset.kind === 'image';
  const inPoint = Number(start), outPoint = Number(end);
  const valid = isImage || (start !== '' && end !== '' && Number.isFinite(inPoint) && Number.isFinite(outPoint) && inPoint >= 0 && outPoint > inPoint && outPoint <= duration);
  const failed = () => {
    if (!isImage && !fallback && src === original && proxy && proxy !== original) { setFallback(true); return; }
    setPreviewError('No se pudo reproducir esta fuente. Cierra y vuelve a abrir la vista previa para reintentar.');
  };
  const loaded = event => {
    if (!duration && positive(event.currentTarget.duration)) {
      setDuration(event.currentTarget.duration); setEnd(String(event.currentTarget.duration));
    }
  };
  return <form className="cmb-form" onSubmit={event => {
    event.preventDefault();
    if (valid && !busy) onInsert(isImage ? undefined : { source_start: inPoint, duration: outPoint - inPoint });
  }}>
    <div className={`cmb-source-well ${asset.kind === 'audio' ? 'cmb-source-audio' : ''}`}>
      {!src ? <p className="cmb-error" role="alert">Fuente no disponible en esta sesion.</p> : isImage
        ? <img src={src} alt={asset.filename} referrerPolicy="no-referrer" onError={failed} />
        : asset.kind === 'audio'
          ? <><AudioWaveform className="cmb-audio-symbol" aria-hidden="true" /><audio key={src} src={src} controls preload="metadata" aria-label={`Fuente ${asset.filename}`} onLoadedMetadata={loaded} onError={failed} /></>
          : <video key={src} src={src} controls playsInline preload="metadata" aria-label={`Fuente ${asset.filename}`} poster={creatorMediaUrl(asset.thumbnail_url, session) || undefined} onLoadedMetadata={loaded} onError={failed} />}
    </div>
    {previewError && <p role="alert" className="cmb-error">{previewError}</p>}
    {!isImage && <>
      <div className="cmb-source-range">
        <label className="cmb-field"><span>In (s)</span><input type="number" aria-label="In (s)" min="0" max={duration || undefined} step="any" value={start} disabled={busy} onChange={event => setStart(event.target.value)} /></label>
        <label className="cmb-field"><span>Out (s)</span><input type="number" aria-label="Out (s)" min="0" max={duration || undefined} step="any" value={end} disabled={busy} onChange={event => setEnd(event.target.value)} /></label>
      </div>
      <output className="cmb-range-duration">{valid ? `Seleccion: ${seconds(outPoint - inPoint)} (${Number((outPoint - inPoint).toFixed(6))} s)` : 'Selecciona un rango dentro de la duracion de la fuente.'}</output>
    </>}
    {error && <p role="alert" className="cmb-error">{error}</p>}
    <div className="cmb-dialog-actions"><button type="button" className="cmb-button" onClick={onCancel}>Cancelar</button>
      <button type="submit" className="cmb-button cmb-primary" disabled={!valid || busy} title={valid ? 'Insertar al cabezal' : 'Selecciona un rango valido'}>
        {busy ? <LoaderCircle className="cmb-spin" /> : <Plus />}Insertar al cabezal</button></div>
  </form>;
}

export default function CreatorMediaBin({ project, session, time = 0, onInsert, onFile, onMediaAction, uploading = false, uploadProgress = 0, uploadStage, onCancelUpload }) {
  const assets = useMemo(() => project.assets || [], [project.assets]);
  const [selected, setSelected] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState('');
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordMounted, setRecordMounted] = useState(false);
  const replaceInput = useRef(null);
  const replaceTarget = useRef(null);
  const focusTarget = useRef(null);
  const bin = useRef(null);
  const operation = useRef(null);
  const mounted = useRef(false);
  const recordId = useId();
  const locked = Boolean(busy || uploading);
  const chosen = assets.filter(asset => selected.includes(asset.id));
  const usage = useMemo(() => {
    const counts = new Map();
    for (const track of project.tracks || []) for (const clip of track.clips || []) counts.set(clip.asset_id, (counts.get(clip.asset_id) || 0) + 1);
    return counts;
  }, [project.tracks]);

  useEffect(() => {
    mounted.current = true;
    setSelected([]); setDialog(null); setBusy(null); setError(null); setToast('');
    setRecordOpen(false); setRecordMounted(false); replaceTarget.current = null;
    return () => { mounted.current = false; operation.current = null; };
  }, [project.project_id]);
  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    const ids = new Set(assets.map(asset => asset.id));
    setSelected(current => current.every(id => ids.has(id)) ? current : current.filter(id => ids.has(id)));
    setDialog(current => current && !ids.has(current.asset.id) ? null : current);
  }, [assets]);

  const toggle = id => setSelected(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  const close = () => setDialog(null);
  const open = (type, asset) => {
    focusTarget.current = bin.current?.querySelector(`[data-media-id="${asset.id}"] .cmb-menu-trigger`) || bin.current;
    setError(null); setDialog({ type, asset });
  };
  const run = async (scope, callback, message, closeAfter = false) => {
    if (operation.current || uploading) return;
    const token = {};
    operation.current = token;
    setBusy(scope); setError(null); setToast('');
    try {
      await callback();
      if (!mounted.current || operation.current !== token) return;
      setToast(message);
      if (closeAfter) close();
    } catch {
      if (mounted.current && operation.current === token) setError({ scope, message: 'No se pudo completar la operacion. Reintenta.' });
    } finally {
      if (mounted.current && operation.current === token) { operation.current = null; setBusy(null); }
    }
  };
  const insert = (items, range, closeAfter = false, mode = 'add') => {
    if (!onInsert || !items.length) return;
    return run(items[0].id, () => mode !== 'add' ? onInsert(items, time, undefined, range, mode) : range ? onInsert(items, time, undefined, range) : onInsert(items, time),
      items.length === 1 ? 'Medio agregado a timeline.' : `${items.length} medios agregados en secuencia.`, closeAfter);
  };
  const action = (type, asset) => {
    if (['add', 'insert', 'overwrite'].includes(type)) { insert([asset], undefined, false, type === 'add' ? 'add' : type); return; }
    if (type === 'replace') {
      replaceTarget.current = asset;
      replaceInput.current.accept = MEDIA_ACCEPT[asset.kind] || 'video/*,audio/*,image/png,image/jpeg,image/webp';
      replaceInput.current.click(); return;
    }
    if (type === 'proxy') { run(asset.id, () => onMediaAction('proxy', asset, {}), 'Proxy solicitado.'); return; }
    open(type, asset);
  };
  const current = dialog && (assets.find(asset => asset.id === dialog.asset.id) || dialog.asset);
  const related = current ? dependencies(project, current) : null;
  const dialogError = error && current && error.scope === current.id ? error.message : '';
  const progress = Math.min(100, Math.max(0, Number(uploadProgress) || 0));

  return <section className="creator-media-bin" aria-label="Biblioteca de medios" data-testid="creator-media-bin" ref={bin} tabIndex={-1} onKeyDown={event => event.stopPropagation()}>
    <div className="cmb-selection-bar">
      <input type="checkbox" aria-label="Seleccionar todos los medios" checked={Boolean(assets.length && chosen.length === assets.length)} disabled={!assets.length || locked}
        ref={node => { if (node) node.indeterminate = chosen.length > 0 && chosen.length < assets.length; }}
        onChange={event => setSelected(event.target.checked ? assets.map(asset => asset.id) : [])} />
      <output aria-label="Medios seleccionados" className="cmb-selection-count">{chosen.length}</output>
      <button type="button" className="cmb-button cmb-add-selected" title="Agregar seleccionados en secuencia" disabled={!chosen.length || locked || !onInsert} onClick={() => insert(chosen)}><Plus />Agregar seleccionados</button>
    </div>
    {uploading && <div className="cmb-upload-progress"><progress aria-label={progress >= 100 ? 'Preparando proxy' : 'Progreso de importacion'} max="100" value={progress} />
      {uploadStage && <span role="status">{progress >= 100 ? creatorUploadStageLabel(uploadStage) : `Subiendo ${progress}%`}</span>}
      {onCancelUpload && <button type="button" className="creator-icon" aria-label="Cancelar carga" title="Cancelar carga" onClick={onCancelUpload}><X /></button>}
    </div>}
    <input className="cmb-file-input" ref={replaceInput} type="file" aria-label="Reemplazar archivo" tabIndex={-1} disabled={locked || !onMediaAction}
      onChange={event => {
        const file = event.target.files?.[0];
        const asset = replaceTarget.current;
        event.target.value = ''; replaceTarget.current = null;
        if (file && asset && onMediaAction) run(asset.id, () => onMediaAction('replace', asset, { file }), 'Archivo reemplazado.');
      }} />
    <div className="cmb-list" aria-label="Medios del proyecto">
      {!assets.length && <div className="cmb-empty"><Film aria-hidden="true" /><span>Sin medios</span></div>}
      {assets.map(asset => {
        const origin = recordingOrigin(asset.filename);
        const count = usage.get(asset.id) || 0;
        const canProxy = asset.proxy_status !== 'ready' && asset.proxy_status !== 'not_required' && Boolean(
          asset.capabilities?.create_proxy === true || project.capabilities?.create_proxy === true || session?.capabilities?.create_proxy === true);
        const menu = Primitive => <MenuActions primitive={Primitive} disabled={locked} canManage={Boolean(onMediaAction)} canInsert={Boolean(onInsert)} canProxy={canProxy} onAction={type => action(type, asset)} />;
        return <ContextMenu.Root key={asset.id}>
          <ContextMenu.Trigger asChild>
            <article className={`cmb-card ${selected.includes(asset.id) ? 'is-selected' : ''}`} data-media-id={asset.id} data-testid={`creator-media-${asset.id}`}
              aria-label={`Medio ${asset.filename}`} tabIndex={0} draggable={!locked} aria-busy={busy === asset.id}
              onDragStart={event => {
                if (locked || interactive(event.target)) { event.preventDefault(); return; }
                event.dataTransfer.setData('application/x-steelmora-asset', asset.id);
                event.dataTransfer.effectAllowed = 'copy';
              }}
              onClick={event => { if (!interactive(event.target)) event.ctrlKey || event.metaKey ? toggle(asset.id) : setSelected([asset.id]); }}
              onDoubleClick={event => { if (!interactive(event.target)) open('source', asset); }}
              onKeyDown={event => {
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter') { event.preventDefault(); open('source', asset); }
                if (event.code === 'Space' || event.key === ' ') { event.preventDefault(); if (!locked) toggle(asset.id); }
              }}>
              <input type="checkbox" aria-label={`Seleccionar ${asset.filename}`} checked={selected.includes(asset.id)} disabled={locked} onChange={() => toggle(asset.id)} />
              <MediaThumbnail asset={asset} session={session} />
              <div className="cmb-card-details">
                <b className="cmb-filename" title={asset.filename}>{asset.filename}</b>
                <div className="cmb-metadata"><span className={`cmb-kind cmb-kind-${asset.kind}`}>{KIND[asset.kind] || asset.kind}</span>{asset.kind !== 'image' && <span>{positive(asset.duration) ? seconds(asset.duration) : 'Duracion no disponible'}</span>}
                  {origin && <span title={`Origen: ${origin.label}`} className="cmb-origin"><origin.Icon />{origin.label}</span>}</div>
                {asset.kind !== 'audio' && <span className="cmb-resolution">{resolution(asset)}</span>}
                <div className="cmb-metadata"><span className="cmb-audio-state">{asset.has_audio ? <Volume2 /> : <VolumeX />}{asset.has_audio === true ? 'Con audio' : asset.has_audio === false ? 'Sin audio' : 'Audio no declarado'}</span><span>{count} {count === 1 ? 'clip' : 'clips'}</span></div>
                <span className={`cmb-proxy ${asset.proxy_status === 'ready' ? 'is-ready' : ''}`}>{busy === asset.id ? <><LoaderCircle className="cmb-spin" />Procesando...</> : proxyLabel(asset)}</span>
              </div>
              <div className="cmb-card-actions">
                <IconButton label={`Agregar ${asset.filename} al cabezal`} disabled={locked || !onInsert} onClick={() => insert([asset])}><Plus /></IconButton>
                <DropdownMenu.Root><DropdownMenu.Trigger asChild><IconButton label={`Acciones de ${asset.filename}`} className="cmb-icon cmb-menu-trigger"><MoreHorizontal /></IconButton></DropdownMenu.Trigger>
                  <DropdownMenu.Portal><DropdownMenu.Content className="cmb-menu" align="end" sideOffset={5} collisionPadding={8} onKeyDown={event => event.stopPropagation()} onCloseAutoFocus={event => { if (dialog) event.preventDefault(); }}>{menu(DropdownMenu)}</DropdownMenu.Content></DropdownMenu.Portal>
                </DropdownMenu.Root>
              </div>
              {error?.scope === asset.id && !dialog && <p className="cmb-error cmb-card-error" role="alert">{error.message}</p>}
            </article>
          </ContextMenu.Trigger>
          <ContextMenu.Portal><ContextMenu.Content className="cmb-menu" collisionPadding={8} onKeyDown={event => event.stopPropagation()} onCloseAutoFocus={event => { if (dialog) event.preventDefault(); }}>{menu(ContextMenu)}</ContextMenu.Content></ContextMenu.Portal>
        </ContextMenu.Root>;
      })}
    </div>
    <div className="cmb-record">
      <button type="button" className="cmb-record-toggle" aria-expanded={recordOpen} aria-controls={recordId} onClick={() => { setRecordMounted(true); setRecordOpen(value => !value); }}>
        <Camera /><span>Grabar</span><ChevronDown className={recordOpen ? 'is-open' : ''} /></button>
      <div id={recordId} className="cmb-record-body" hidden={!recordOpen}>{recordMounted && <CreatorCapture onFile={onFile} disabled={locked || !onFile} />}</div>
    </div>
    <div role="status" aria-live="polite" aria-atomic="true" className={`cmb-toast ${toast ? 'is-visible' : ''}`}>{toast && <><Check aria-hidden="true" /><span>{toast}</span></>}</div>
    {dialog && current && <Modal title={{ info: 'Informacion del medio', rename: 'Renombrar medio', delete: 'Eliminar medio', source: 'Monitor de fuente' }[dialog.type]}
      description={current.filename} onClose={close} source={dialog.type === 'source'} returnFocus={focusTarget.current || bin.current}>
      {dialog.type === 'info' && <MediaInfo asset={current} project={project} />}
      {dialog.type === 'rename' && <RenameForm key={current.id} asset={current} busy={locked} error={dialogError} onCancel={close} onClearError={() => setError(null)}
        onSave={filename => run(current.id, () => onMediaAction('rename', current, { filename }), 'Nombre actualizado.', true)} />}
      {dialog.type === 'source' && <SourceMonitor key={current.id} asset={current} session={session} busy={locked || !onInsert} error={dialogError} onCancel={close} onInsert={range => insert([current], range, true)} />}
      {dialog.type === 'delete' && <div className="cmb-form">
        <p className="cmb-delete-message">{related.direct ? `Este archivo se utiliza en ${related.direct} clips.` : 'Este archivo no se utiliza directamente en la timeline.'}</p>
        {related.derived > 0 && <p className="cmb-dependencies">{related.derived} medios derivados y {related.derivedClips} clips derivados dependen de este archivo.</p>}
        {related.beatGrid > 0 && <p className="cmb-dependencies">Una cuadricula de beats depende de este archivo.</p>}
        {dialogError && <p role="alert" className="cmb-error">{dialogError}</p>}
        <div className="cmb-dialog-actions"><button type="button" className="cmb-button" onClick={close}>Cancelar</button>
          <button type="button" className="cmb-button cmb-destructive" disabled={locked} onClick={() => run(current.id, () => onMediaAction('delete', current, { remove_clips: related.used }), 'Medio eliminado.', true)}>
            {busy ? <LoaderCircle className="cmb-spin" /> : <Trash2 />}{related.used ? 'Eliminar tambien los clips' : 'Eliminar medio'}</button></div>
      </div>}
    </Modal>}
  </section>;
}
